import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { parseISO, format, isSameDay, isYesterday } from "date-fns";
import { es } from "date-fns/locale";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import WhatsAppAvatar from "../WhatsAppAvatar";
import { formatContactName } from "../../utils/formatPhoneNumber";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
  },
  list: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  listItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  messageSnippet: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    lineHeight: 1.4,
    marginTop: 2,
  },
  highlight: {
    backgroundColor: "rgba(255, 193, 7, 0.6)",
    fontWeight: 600,
    padding: "0 1px",
    borderRadius: 2,
  },
  time: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  noResults: {
    textAlign: "center",
    padding: 40,
    color: theme.palette.text.secondary,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: 16,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },
}));

/** Highlights search term in text (case-insensitive) */
function HighlightMatch({ text, searchTerm, highlightClassName }) {
  if (!text || !searchTerm) return <>{text}</>;
  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = String(text).split(regex);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className={highlightClassName}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

const MessageSearchResultItem = ({ result, searchTerm, onSelect }) => {
  const classes = useStyles();
  const contactName =
    result.ticket?.contact?.name || result.ticket?.contact?.number || "";

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = parseISO(dateString);
    const now = new Date();
    if (isSameDay(date, now)) {
      return format(date, "h:mm a", { locale: es });
    }
    if (isYesterday(date)) {
      return "ayer " + format(date, "h:mm a", { locale: es });
    }
    return format(date, "dd/MM h:mm", { locale: es });
  };

  const snippet = result.body || "";
  const maxSnippetLen = 120;
  const truncated =
    snippet.length > maxSnippetLen
      ? snippet.slice(0, maxSnippetLen) + "..."
      : snippet;

  return (
    <>
      <ListItem
        button
        className={classes.listItem}
        onClick={() => onSelect(result.ticketId, result.id)}
      >
        <ListItemAvatar>
          <WhatsAppAvatar
            src={result.ticket?.contact?.profilePicUrl}
            name={contactName}
            size={44}
          />
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <div className={classes.headerRow}>
              <Typography className={classes.contactName} noWrap>
                {formatContactName(contactName)}
              </Typography>
              <Typography className={classes.time}>
                {formatTime(result.createdAt)}
              </Typography>
            </div>
          }
          secondary={
            <Typography className={classes.messageSnippet} component="span">
              <HighlightMatch
                text={truncated}
                searchTerm={searchTerm}
                highlightClassName={classes.highlight}
              />
            </Typography>
          }
        />
      </ListItem>
      <Divider variant="inset" component="li" />
    </>
  );
};

const MessageSearchResults = ({ searchParam, selectedQueueIds }) => {
  const classes = useStyles();
  const history = useHistory();
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchResults = useCallback(
    async (pageNum = 1, append = false) => {
      if (!searchParam || !searchParam.trim()) {
        setResults([]);
        setHasMore(false);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get("/messages/search", {
          params: {
            q: searchParam.trim(),
            page: pageNum,
            limit: 20,
            queueIds: JSON.stringify(selectedQueueIds || []),
          },
        });
        const newItems = data.messages || [];
        setResults((prev) => (append ? [...prev, ...newItems] : newItems));
        setHasMore(data.hasMore || false);
      } catch (err) {
        toastError(err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [searchParam, selectedQueueIds]
  );

  useEffect(() => {
    setPage(1);
    fetchResults(1, false);
  }, [searchParam, selectedQueueIds, fetchResults]);

  const loadMore = () => {
    if (!hasMore || loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchResults(nextPage, true);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 80) < clientHeight) {
      loadMore();
    }
  };

  const handleSelectResult = (ticketId, messageId) => {
    history.push(`/tickets/${ticketId}?messageId=${messageId}`);
  };

  return (
    <Paper className={classes.wrapper}>
      <List
        className={classes.list}
        onScroll={handleScroll}
        style={{ paddingTop: 0 }}
      >
        {!searchParam || !searchParam.trim() ? (
          <div className={classes.noResults}>
            {searchParam === "" ? "" : "Escribe un término para buscar"}
          </div>
        ) : results.length === 0 && !loading ? (
          <div className={classes.noResults}>
            {i18n.t("tickets.search.noResults")}
          </div>
        ) : (
          results.map((r) => (
            <MessageSearchResultItem
              key={`${r.ticketId}-${r.id}`}
              result={r}
              searchTerm={searchParam}
              onSelect={handleSelectResult}
            />
          ))
        )}
        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        )}
      </List>
    </Paper>
  );
};

export default MessageSearchResults;
